// Google Gemini API Configuration
const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || 'AIzaSyAOSvJFZM2K3a4dOSR5Mch8K_Ij7VR1MV4'
const GEMINI_API_ENDPOINT = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent'
const GEMINI_MODEL = 'gemini-2.5-flash-lite' // Google Gemini 2.5 Flash Lite - Cost-efficient, high-throughput

// Cache for roadmap generation (topic + constraints -> roadmap)
const roadmapCache = new Map()

// Generate cache key from onboarding data
const getCacheKey = (onboardingData) => {
  return JSON.stringify({
    topic: onboardingData.topic,
    level: onboardingData.currentLevel,
    hours: onboardingData.weeklyHours,
    weeks: onboardingData.deadline,
    style: onboardingData.learningStyle
  })
}

// Note: JSON Schema enforcement is handled via response_format: { type: "json_object" }
// Full schema validation happens in the prompt and post-processing

// Clean JSON response - remove markdown fences and extra text
const cleanJSONResponse = (text) => {
  let cleaned = text.trim()
  
  // Remove markdown code fences
  cleaned = cleaned.replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/\s*```$/, '')
  
  // Find first { and last }
  const firstBrace = cleaned.indexOf('{')
  const lastBrace = cleaned.lastIndexOf('}')
  
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    cleaned = cleaned.substring(firstBrace, lastBrace + 1)
  }
  
  // Remove trailing commas
  cleaned = cleaned.replace(/,(\s*[}\]])/g, '$1')
  
  // Fix smart quotes
  cleaned = cleaned.replace(/[""]/g, '"').replace(/['']/g, "'")
  
  return cleaned
}

// Note: URL reachability check removed due to CORS limitations
// URLs are validated for format only, not actual reachability

// Fallback URLs for common topics - using search URLs instead of specific videos
const FALLBACK_URLS = {
  python: 'https://www.youtube.com/results?search_query=python+programming+tutorial',
  javascript: 'https://www.youtube.com/results?search_query=javascript+tutorial',
  react: 'https://www.youtube.com/results?search_query=react+tutorial',
  data: 'https://www.youtube.com/results?search_query=data+analysis+tutorial',
  web: 'https://www.youtube.com/results?search_query=web+development+tutorial',
  accounting: 'https://www.youtube.com/results?search_query=accounting+basics+tutorial',
  default: 'https://www.youtube.com/results?search_query=tutorial'
}

// Convert specific YouTube video URLs to search URLs
const convertYouTubeToSearch = (url, taskTitle) => {
  try {
    const urlObj = new URL(url)
    // If it's a specific video URL, convert to search
    if (urlObj.hostname.includes('youtube.com') && urlObj.pathname.includes('/watch')) {
      const searchQuery = encodeURIComponent(taskTitle.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').trim().replace(/\s+/g, '+'))
      return `https://www.youtube.com/results?search_query=${searchQuery}+tutorial`
    }
    return url
  } catch (error) {
    return url
  }
}

// Verify and fix resource URLs in roadmap
const verifyResourceURLs = async (roadmap) => {
  console.log('🔍 Verifying resource URLs...')
  let verifiedCount = 0
  let placeholderCount = 0
  
  for (const week of roadmap.weeks) {
    for (const task of week.tasks) {
      if (!task.resources || !Array.isArray(task.resources)) continue
      
      for (const resource of task.resources) {
        if (!resource.url) {
          resource.is_placeholder = true
          resource.url = FALLBACK_URLS.default
          placeholderCount++
          continue
        }
        
        // Check if URL is valid format
        try {
          new URL(resource.url)
          
          // Convert specific YouTube video URLs to search URLs
          const originalUrl = resource.url
          resource.url = convertYouTubeToSearch(resource.url, task.title)
          
          if (originalUrl !== resource.url) {
            console.log(`🔄 Converted YouTube video to search: ${task.title}`)
          }
          
          verifiedCount++
          resource.is_placeholder = false
        } catch (error) {
          console.warn(`⚠️ Invalid URL format: ${resource.url}`)
          resource.is_placeholder = true
          
          // Try to find appropriate fallback
          const topic = roadmap.topic.toLowerCase()
          for (const [key, fallbackUrl] of Object.entries(FALLBACK_URLS)) {
            if (topic.includes(key)) {
              resource.url = fallbackUrl
              break
            }
          }
          if (resource.is_placeholder) {
            resource.url = FALLBACK_URLS.default
          }
          placeholderCount++
        }
      }
    }
  }
  
  console.log(`✅ Verified ${verifiedCount} URLs, ${placeholderCount} placeholders`)
  return roadmap
}

const SYSTEM_PROMPT = `You are PathPilot AI, an expert learning architect and mentor who creates personalized, week-by-week learning roadmaps.

Your mission: Design comprehensive, actionable roadmaps that transform beginners into skilled practitioners through structured, progressive learning.

REQUIRED JSON STRUCTURE:

{
  "title": "string (engaging roadmap title, e.g., 'Complete Python Programming Mastery')",
  "topic": "string (learning topic)",
  "duration_weeks": number,
  "confidence": number (0-100, your confidence this roadmap will succeed)",
  "why_this_fits": "string (2-3 sentences explaining why this roadmap matches the learner's profile)",
  "weeks": [
    {
      "week_number": number (1, 2, 3...),
      "theme": "string (compelling weekly theme, e.g., 'Python Fundamentals & Syntax Mastery')",
      "tasks": [
        {
          "id": "string (format: 'week1-task1', 'week1-task2')",
          "title": "string (clear, actionable title)",
          "description": "string (2-3 detailed sentences: what to learn, why it matters, how it connects)",
          "est_hours": number (realistic estimate),
          "deliverable": "string (specific, measurable outcome)",
          "resources": [
            {
              "title": "string (descriptive resource name)",
              "url": "string (REAL, working URL - verify it exists)",
              "type": "string (documentation/course/video/tutorial/article)"
            }
          ]
        }
      ]
    }
  ]
}

EXCELLENCE STANDARDS:

1. **Completeness**: Generate EVERY week from 1 to N. No gaps, no shortcuts.

2. **Task Quality**:
   - Titles: Action-oriented (e.g., "Master Python Variables and Data Types")
   - Descriptions: Explain WHAT to learn, WHY it matters, HOW it connects to goals
   - Hours: Realistic estimates that sum to weekly commitment
   - Deliverables: Concrete, measurable (e.g., "5 working Python scripts", "Portfolio website deployed")

3. **Resource Excellence**:
   - 3-4 resources per task
   - Mix types: documentation + course + video
   - Priority order:
     a) Official docs (Python.org, MDN, React.dev)
     b) Free courses (freeCodeCamp, Coursera, edX, Khan Academy)
     c) YouTube SEARCH URLs (never specific videos)
   - All URLs must be real and accessible
   - Each resource must add unique value

4. **Learning Progression**:
   - Week 1-2: Foundations and basics
   - Week 3-5: Core concepts and practice
   - Week 6-8: Intermediate skills and projects
   - Week 9+: Advanced topics and portfolio work
   - Build complexity gradually
   - Review and reinforce previous concepts

5. **Engagement**:
   - Themes: Exciting and motivating
   - Descriptions: Clear and encouraging
   - Projects: Practical and portfolio-worthy
   - Pacing: Challenging but achievable

6. **Personalization**:
   - Adapt to learner's level (beginner/intermediate/advanced)
   - Respect time constraints
   - Honor learning style preferences
   - Account for stated constraints

7. **Quality Assurance**:
   - Every week has meaningful content
   - No placeholder or fake URLs
   - Descriptions are helpful, not generic
   - Hours are realistic and balanced
   - Deliverables are specific and valuable

MENTOR MINDSET:
- Be supportive and encouraging
- Explain concepts clearly
- Build confidence progressively
- Celebrate milestones
- Make learning enjoyable

OUTPUT FORMAT:
Return ONLY the JSON object. No markdown code blocks, no explanations, no extra text. Just pure, valid JSON.`

export const callLLM = async (messages, retryCount = 0) => {
  const maxRetries = 3
  const baseDelay = 2000 // 2 seconds
  
  try {
    console.log(`🤖 Generating roadmap with AI...`)
    
    if (!GEMINI_API_KEY) {
      throw new Error('AI service not configured. Please contact support.')
    }
    
    // Convert messages to Gemini format
    // Gemini uses "parts" array with "text" field
    const geminiMessages = messages.map(msg => ({
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: msg.content }]
    }))
    
    // Add system prompt as first user message
    const allMessages = [
      {
        role: 'user',
        parts: [{ text: SYSTEM_PROMPT }]
      },
      ...geminiMessages
    ]
    
    const response = await fetch(`${GEMINI_API_ENDPOINT}?key=${GEMINI_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contents: allMessages,
        generationConfig: {
          temperature: 0.7,
          topP: 0.9,
          topK: 40,
          maxOutputTokens: 8192,
          responseMimeType: 'application/json'
        },
        safetySettings: [
          {
            category: 'HARM_CATEGORY_HARASSMENT',
            threshold: 'BLOCK_NONE'
          },
          {
            category: 'HARM_CATEGORY_HATE_SPEECH',
            threshold: 'BLOCK_NONE'
          },
          {
            category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT',
            threshold: 'BLOCK_NONE'
          },
          {
            category: 'HARM_CATEGORY_DANGEROUS_CONTENT',
            threshold: 'BLOCK_NONE'
          }
        ]
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.error?.message || `API error: ${response.status}`
      
      // Handle rate limiting with exponential backoff
      if (response.status === 429) {
        if (retryCount < maxRetries) {
          const delay = baseDelay * Math.pow(2, retryCount)
          console.log(`⏳ Rate limit hit. Retrying in ${delay/1000}s... (attempt ${retryCount + 1}/${maxRetries})`)
          await new Promise(resolve => setTimeout(resolve, delay))
          return callLLM(messages, retryCount + 1)
        }
        throw new Error('Rate limit reached. Please wait a moment and try again.')
      } else if (response.status === 401 || response.status === 403) {
        throw new Error('API authentication failed. Please check your API key.')
      } else if (response.status === 503) {
        throw new Error('Service temporarily unavailable. Please try again shortly.')
      }
      
      throw new Error(errorMessage)
    }

    const data = await response.json()
    
    if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
      throw new Error('Invalid response format from Gemini API')
    }

    const content = data.candidates[0].content.parts[0].text

    console.log(`✅ Roadmap generated successfully`)
    
    return {
      content: content,
      model: GEMINI_MODEL,
      error: null
    }
    
  } catch (error) {
    console.error(`❌ AI generation error:`, error.message)
    
    return {
      content: null,
      model: null,
      error: error.message || 'I couldn\'t generate the roadmap right now — please try again shortly.'
    }
  }
}

export const generateRoadmap = async (onboardingData) => {
  // Check cache first
  const cacheKey = getCacheKey(onboardingData)
  if (roadmapCache.has(cacheKey)) {
    console.log('✅ Returning cached roadmap')
    return { roadmap: roadmapCache.get(cacheKey), error: null }
  }

  const weeks = parseInt(onboardingData.deadline)
  const hoursPerWeek = parseInt(onboardingData.weeklyHours)
  const tasksPerWeek = weeks <= 8 ? 2 : weeks <= 16 ? 2 : 1
  
  const prompt = `Create a comprehensive ${weeks}-week learning roadmap for: "${onboardingData.topic}"

LEARNER PROFILE:
- Current Level: ${onboardingData.currentLevel}
- Weekly Commitment: ${hoursPerWeek} hours/week
- Total Duration: ${weeks} weeks
- Learning Style: ${onboardingData.learningStyle || 'Balanced Mix'}
${onboardingData.preferences ? `- Preferences: ${onboardingData.preferences}` : ''}
${onboardingData.constraints ? `- Constraints: ${onboardingData.constraints}` : ''}

ROADMAP REQUIREMENTS:
1. Generate ALL ${weeks} weeks (week_number: 1 to ${weeks})
2. Each week must have:
   - week_number: Sequential number (1, 2, 3...)
   - theme: Clear, engaging weekly focus (e.g., "Python Fundamentals", "Building Your First App")
   - ${tasksPerWeek} tasks per week

3. Each task must include:
   - id: Unique identifier (e.g., "week1-task1")
   - title: Clear, actionable task name
   - description: Detailed 2-3 sentence explanation of what to learn and why
   - est_hours: Realistic time estimate (distribute ${hoursPerWeek}h across tasks)
   - deliverable: Specific, measurable outcome (e.g., "Working calculator app", "10 solved exercises")
   - resources: Array of 3-4 high-quality learning resources

4. RESOURCE QUALITY STANDARDS:
   Priority 1 - Official Documentation:
   - Python: https://docs.python.org/3/
   - JavaScript: https://developer.mozilla.org/en-US/docs/Web/JavaScript
   - React: https://react.dev/
   - Node.js: https://nodejs.org/docs/
   
   Priority 2 - Free Courses:
   - freeCodeCamp: https://www.freecodecamp.org/
   - Coursera (audit mode): https://www.coursera.org/
   - edX (free track): https://www.edx.org/
   - Khan Academy: https://www.khanacademy.org/
   
   Priority 3 - YouTube Search (NOT specific videos):
   - Format: https://www.youtube.com/results?search_query=[topic]+tutorial
   - Example: https://www.youtube.com/results?search_query=python+basics+tutorial
   
   Each resource needs:
   - title: Descriptive name
   - url: Real, working URL
   - type: "documentation", "course", "video", "tutorial", or "article"

5. LEARNING PROGRESSION:
   - Start with fundamentals, build to advanced
   - Each week builds on previous weeks
   - Include hands-on projects every 2-3 weeks
   - Mix theory with practice
   - Ensure realistic pacing for ${hoursPerWeek}h/week

6. QUALITY CHECKS:
   - All ${weeks} weeks must be present
   - Tasks should total ~${hoursPerWeek}h per week
   - Descriptions must be helpful and specific
   - Resources must be real URLs (no placeholders)
   - Deliverables must be concrete and achievable

Generate a complete, high-quality roadmap that will genuinely help someone learn ${onboardingData.topic}.`

  let retryCount = 0
  const maxRetries = 2

  while (retryCount <= maxRetries) {
    try {
      const result = await callLLM([
        { role: 'user', content: retryCount === 0 ? prompt : `${prompt}\n\nYou MUST return valid JSON following the schema exactly. Do not include any extra text.` }
      ])

      if (result.error) {
        throw new Error(result.error)
      }

      // Clean and parse JSON
      let jsonContent = cleanJSONResponse(result.content)
      
      let roadmap
      try {
        roadmap = JSON.parse(jsonContent)
      } catch (parseError) {
        console.warn(`⚠️ JSON parse failed (attempt ${retryCount + 1}/${maxRetries + 1}):`, parseError.message)
        console.error('Raw response (first 500 chars):', result.content?.substring(0, 500))
        
        if (retryCount < maxRetries) {
          retryCount++
          console.log(`🔄 Retrying (attempt ${retryCount + 1}/${maxRetries + 1})...`)
          await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1s before retry
          continue
        }
        throw new Error('Failed to parse roadmap JSON after multiple attempts')
      }
      
      // Validate and fix required fields
      if (!roadmap.title) {
        roadmap.title = `${onboardingData.topic} Learning Roadmap`
      }
      if (!roadmap.topic) {
        roadmap.topic = onboardingData.topic
      }
      if (!roadmap.weeks || !Array.isArray(roadmap.weeks) || roadmap.weeks.length === 0) {
        console.warn('⚠️ No weeks array found, roadmap may be incomplete')
        throw new Error('Roadmap missing weeks data')
      }
      
      // Validate weeks array structure
      if (!Array.isArray(roadmap.weeks)) {
        throw new Error('Weeks must be an array')
      }
      
      // Ensure each week has tasks array
      for (const week of roadmap.weeks) {
        if (!week.tasks || !Array.isArray(week.tasks)) {
          week.tasks = []
        }
        // Ensure each task has resources array
        for (const task of week.tasks) {
          if (!task.resources || !Array.isArray(task.resources)) {
            task.resources = []
          }
        }
      }
      
      // CRITICAL: Validate weeks count matches requested duration
      const expectedWeeks = parseInt(onboardingData.deadline)
      if (roadmap.weeks.length < expectedWeeks) {
        console.warn(`⚠️ Incomplete roadmap: got ${roadmap.weeks.length} weeks, expected ${expectedWeeks}`)
        
        // Only retry if we got very few weeks (less than 30% of expected)
        if (roadmap.weeks.length < expectedWeeks * 0.3 && retryCount < maxRetries) {
          retryCount++
          console.log(`🔄 Retrying to get all ${expectedWeeks} weeks...`)
          continue
        }
        
        // If we got at least 30% of weeks, accept it with warning
        if (roadmap.weeks.length < expectedWeeks) {
          console.warn(`⚠️ Accepting partial roadmap: ${roadmap.weeks.length}/${expectedWeeks} weeks`)
        }
      }
      
      if (!roadmap.duration_weeks) {
        roadmap.duration_weeks = roadmap.weeks.length
      }
      if (!roadmap.confidence) {
        roadmap.confidence = 0.8
      }
      if (!roadmap.why_this_fits) {
        roadmap.why_this_fits = `Tailored roadmap for ${onboardingData.currentLevel} level`
      }
      
      // Verify and fix resource URLs
      roadmap = await verifyResourceURLs(roadmap)
      
      // Add metadata
      roadmap.id = `roadmap_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      roadmap.created_at = new Date().toISOString()
      roadmap.model_used = result.model
      roadmap.onboarding_data = onboardingData
      
      // Cache the result
      roadmapCache.set(cacheKey, roadmap)
      
      console.log(`✅ Roadmap generated successfully: ${roadmap.weeks.length} weeks, ${roadmap.title}`)
      
      return { roadmap, error: null }
      
    } catch (error) {
      if (retryCount < maxRetries) {
        retryCount++
        console.log(`🔄 Retrying (attempt ${retryCount + 1}/${maxRetries + 1})...`)
        continue
      }
      
      console.error('❌ Failed to generate roadmap after retries:', error.message)
      return { 
        roadmap: null, 
        error: 'Failed to generate roadmap. Please try again.' 
      }
    }
  }
}

export const editRoadmapWithLLM = async (currentRoadmap, userRequest) => {
  const prompt = `Current roadmap:
${JSON.stringify(currentRoadmap, null, 2)}

User request: ${userRequest}

Modify the roadmap according to the user's request. Maintain the same JSON structure and ensure all resources remain real and verified. Respond ONLY with the complete updated JSON.`

  const result = await callLLM([
    { role: 'user', content: prompt }
  ])

  if (result.error) {
    throw new Error(result.error)
  }

  try {
    let jsonContent = result.content.trim()
    
    if (jsonContent.startsWith('```')) {
      jsonContent = jsonContent.replace(/```json\n?/g, '').replace(/```\n?/g, '')
    }
    
    const updatedRoadmap = JSON.parse(jsonContent)
    updatedRoadmap.updated_at = new Date().toISOString()
    updatedRoadmap.id = currentRoadmap.id
    
    return { roadmap: updatedRoadmap, error: null }
  } catch (parseError) {
    console.error('Failed to parse LLM response:', parseError)
    return { 
      roadmap: null, 
      error: 'Failed to update roadmap. Please try again.' 
    }
  }
}

export const chatWithLLM = async (messages) => {
  const result = await callLLM(messages)
  
  if (result.error) {
    throw new Error(result.error)
  }
  
  return result.content
}
